import { useState } from "react";
import { toast } from "sonner";

export const AdminMedia = () => {
  const [images, setImages] = useState(() => {
    try { return JSON.parse(localStorage.getItem("paradigm_media") || "[]"); } catch { return []; }
  });
  const [urlInput, setUrlInput] = useState("");
  const [filter, setFilter] = useState("all");

  const saveImages = (imgs) => {
    setImages(imgs);
    localStorage.setItem("paradigm_media", JSON.stringify(imgs));
  };

  const addFromUrl = () => {
    if (!urlInput.trim()) return toast.error("Enter an image URL");
    const ext = urlInput.split(".").pop()?.split("?")[0]?.toLowerCase() || "jpg";
    const img = {
      id: Date.now().toString(),
      filename: `image-${Date.now()}.${ext}`,
      url: urlInput.trim(),
      type: (() => { if (ext === "mp4" || ext === "webm") return "video"; if (ext === "pdf") return "document"; return "image"; })(),
      uploadDate: new Date().toISOString(),
    };
    saveImages([img, ...images]);
    setUrlInput("");
    toast.success("Added to media library!");
  };

  const deleteImage = (id) => {
    if (!window.confirm("Remove from library?")) return;
    saveImages(images.filter(i => i.id !== id));
    toast.success("Removed");
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("URL copied!")).catch(() => toast.error("Failed to copy"));
  };

  const filtered = filter === "all" ? images : images.filter(i => i.type === filter);

  return (
    <div>
      {/* Upload Area */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: "1.5rem" }}>
        <h3 style={{ fontSize: ".95rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1rem" }}>Add Media</h3>
        <div style={{ display: "flex", gap: ".75rem", alignItems: "end" }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", fontSize: ".8rem", fontWeight: 600, color: "#374151", marginBottom: ".3rem" }}>Image / Media URL</label>
            <input value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder="Paste image URL here..." onKeyDown={e => e.key === "Enter" && addFromUrl()}
              style={{ width: "100%", padding: ".55rem .75rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem" }} />
          </div>
          <button onClick={addFromUrl} style={{ padding: ".55rem 1.25rem", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #EC4899, #F97316)", color: "#fff", cursor: "pointer", fontSize: ".82rem", fontWeight: 600, height: 38, whiteSpace: "nowrap" }}>
            <i className="fas fa-plus" /> Add
          </button>
        </div>
        <p style={{ fontSize: ".75rem", color: "#9CA3AF", marginTop: ".5rem" }}>
          <i className="fas fa-info-circle" /> Paste direct image URLs from Unsplash, Pexels, or any public source. Storage: {images.length} items
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: ".4rem", marginBottom: "1.25rem" }}>
        {[
          { id: "all", label: `All (${images.length})` },
          { id: "image", label: `Images (${images.filter(i => i.type === "image").length})` },
          { id: "video", label: `Videos (${images.filter(i => i.type === "video").length})` },
          { id: "document", label: `Docs (${images.filter(i => i.type === "document").length})` },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            padding: ".35rem .8rem", borderRadius: 6, border: "none", fontSize: ".78rem", fontWeight: 500, cursor: "pointer",
            background: filter === f.id ? "#1F2937" : "#E5E7EB", color: filter === f.id ? "#fff" : "#6B7280",
          }}>{f.label}</button>
        ))}
      </div>

      {/* Media Grid */}
      {filtered.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, padding: "3rem", textAlign: "center", color: "#9CA3AF", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <i className="fas fa-photo-video" style={{ fontSize: "2.5rem", marginBottom: ".75rem", display: "block" }} />
          <p style={{ fontWeight: 500 }}>No media files yet</p>
          <p style={{ fontSize: ".82rem" }}>Add images by pasting URLs above</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "1rem" }}>
          {filtered.map(img => (
            <div key={img.id} style={{ background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.06)", border: "1px solid #E5E7EB" }}>
              {img.type === "image" ? (
                <img src={img.url} alt={img.filename} style={{ width: "100%", height: 140, objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} />
              ) : (
                <div style={{ width: "100%", height: 140, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`fas ${img.type === "video" ? "fa-video" : "fa-file-pdf"}`} style={{ fontSize: "2rem", color: "#9CA3AF" }} />
                </div>
              )}
              <div style={{ padding: ".6rem .7rem" }}>
                <p style={{ fontSize: ".72rem", color: "#6B7280", margin: "0 0 .4rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{img.filename}</p>
                <div style={{ display: "flex", gap: ".3rem" }}>
                  <button onClick={() => copyUrl(img.url)} title="Copy URL" style={{ flex: 1, padding: ".3rem", borderRadius: 4, border: "1px solid #E5E7EB", background: "#fff", cursor: "pointer", color: "#6B7280", fontSize: ".7rem" }}>
                    <i className="fas fa-link" /> Copy
                  </button>
                  <button onClick={() => deleteImage(img.id)} title="Delete" style={{ padding: ".3rem .5rem", borderRadius: 4, border: "1px solid #FEE2E2", background: "#FEF2F2", cursor: "pointer", color: "#EF4444", fontSize: ".7rem" }}>
                    <i className="fas fa-trash" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
